import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  LocalShipping as DeliveryIcon,
  AttachMoney as RevenueIcon,
  Warning as WarningIcon,
  Notifications as NotificationIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DashboardHome = () => {
  const navigate = useNavigate();

  const stats = [
    {
      title: 'Total Revenue',
      value: '$124,750',
      change: '+12.5%',
      trend: 'up',
      icon: <RevenueIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: 'success.main',
    },
    {
      title: 'Active Orders',
      value: '156',
      change: '+8.2%',
      trend: 'up',
      icon: <OrdersIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: 'primary.main',
    },
    {
      title: 'Inventory Items',
      value: '2,847',
      change: '-2.1%',
      trend: 'down',
      icon: <InventoryIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      color: 'info.main',
    },
    {
      title: 'Pending Deliveries',
      value: '23',
      change: '+15.7%',
      trend: 'up',
      icon: <DeliveryIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: 'warning.main',
    },
  ];

  const recentOrders = [
    {
      id: 'ORD-001',
      customer: 'John Smith',
      product: 'Sofa Set - Modern Gray',
      amount: '$2,450',
      status: 'Processing',
      date: '2024-01-15',
    },
    {
      id: 'ORD-002',
      customer: 'Sarah Johnson',
      product: 'Dining Table - Oak Wood',
      amount: '$1,890',
      status: 'Shipped',
      date: '2024-01-14',
    },
    {
      id: 'ORD-003',
      customer: 'Mike Wilson',
      product: 'Bed Frame - Queen Size',
      amount: '$890',
      status: 'Delivered',
      date: '2024-01-13',
    },
    {
      id: 'ORD-004',
      customer: 'Emily Davis',
      product: 'Coffee Table - Glass Top',
      amount: '$320',
      status: 'Processing',
      date: '2024-01-12',
    },
  ];

  const lowStockItems = [
    { name: 'Accent Chair - Blue', stock: 3, minStock: 10 },
    { name: 'Floor Lamp - Modern', stock: 1, minStock: 5 },
    { name: 'Bookshelf - White', stock: 2, minStock: 8 },
    { name: 'Ottoman - Leather', stock: 4, minStock: 12 },
  ];

  const alerts = [
    {
      id: 1,
      type: 'warning',
      message: '4 items are running low on stock',
      time: '2 hours ago',
    },
    {
      id: 2,
      type: 'info',
      message: 'New order received from premium customer',
      time: '3 hours ago',
    },
    {
      id: 3,
      type: 'success',
      message: 'Monthly sales target achieved',
      time: '1 day ago',
    },
  ];

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'processing':
        return 'warning';
      case 'shipped':
        return 'info';
      case 'delivered':
        return 'success';
      default:
        return 'default';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <NotificationIcon color="info" />;
      case 'success':
        return <TrendingUpIcon color="success" />;
      default:
        return <NotificationIcon />;
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Dashboard Overview
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  {stat.icon}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {stat.trend === 'up' ? (
                      <TrendingUpIcon color="success" fontSize="small" />
                    ) : (
                      <TrendingDownIcon color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: stat.trend === 'up' ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                        ml: 0.5,
                      }}
                    >
                      {stat.change}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Orders */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                  Recent Orders
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/dashboard/orders')}
                >
                  View All
                </Button>
              </Box>
              <List>
                {recentOrders.map((order, index) => (
                  <ListItem
                    key={order.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    secondaryAction={
                      <IconButton edge="end">
                        <MoreIcon />
                      </IconButton>
                    }
                  >
                    <Avatar sx={{ mr: 2, backgroundColor: 'primary.main' }}>
                      {order.customer.charAt(0)}
                    </Avatar>
                    <ListItemText
                      primary={order.id}
                      secondary={`${order.customer} - ${order.product} | ${order.date} • ${order.amount}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Low Stock Alerts */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                  Low Stock Alert
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/dashboard/inventory')}
                >
                  View All
                </Button>
              </Box>
              <List>
                {lowStockItems.map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText
                      primary={item.name}
                      secondary={`Stock: ${item.stock} / ${item.minStock}`}
                    />
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Stock: {item.stock} / {item.minStock}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: item.stock <= item.minStock / 2 ? 'error.main' : 'warning.main' }}
                        >
                          {Math.round((item.stock / item.minStock) * 100)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(item.stock / item.minStock) * 100}
                        color={item.stock <= item.minStock / 2 ? 'error' : 'warning'}
                      />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
                Recent Alerts
              </Typography>
              <List>
                {alerts.map((alert) => (
                  <ListItem key={alert.id} sx={{ px: 0 }}>
                    <Box sx={{ mr: 2 }}>
                      {getAlertIcon(alert.type)}
                    </Box>
                    <ListItemText
                      primary={alert.message}
                      secondary={alert.time}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardHome;
